import * as React from "react";
import { makeStyles } from "@material-ui/core";
import cx from "classnames";

export interface CarouselProps {
  leftControl?: React.ReactNode;
  rightControl?: React.ReactNode;

  onLeftControlClick?: () => void;
  onRightControlClick?: () => void;

  // Each child will be rendered as its own element in the carousel
  children: JSX.Element[];
  // The page index is managed within the Carousel itself, but if the parent component
  // wants to change the index itself, changing this value will do so.
  pageIndex?: number;
  // If specified, then the automatic calculation of the largest element does not happen.
  // These dimensions are placed on the sliding window and each page.
  windowDimensions?: {
    width: number;
    height: number;
  };
}

const useStyles = makeStyles({
  container: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  // The navigation buttons for going left/right
  control: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    position: "absolute",
    top: "50%",
  },
  leftControl: {
    left: -5, // Make space between control and window
    transform: "translate(-100%, -50%)",
  },
  rightControl: {
    right: -5, // Make space between control and window
    transform: "translate(100%, -50%)",
  },
  controlHidden: {
    visibility: "hidden",
  },

  // We absolutely position the positioningContainer to the carouselViewport.
  // Each element in the carousel is called a "page".
  carouselViewport: {
    position: "relative",
    overflow: "hidden",
  },
  positioningContainer: {
    display: "flex",
    position: "absolute",
    left: 0,
    // https://easings.net/#easeOutQuart
    transition: "left 400ms cubic-bezier(0.25, 1, 0.5, 1)",
  },
});

type MAX_WIDTH_HEIGHT = undefined | number;

// This Carousel will *not* be performant with a large number of pages (i.e. children).
// It is not virtualized, so every child is rendered at the same time. If a more performant Carousel is needed,
// then the list we slide around should be virtualized.
export const Carousel = ({
  leftControl,
  rightControl,
  onLeftControlClick,
  onRightControlClick,
  pageIndex = 0,
  windowDimensions,
  children,
}: CarouselProps) => {
  const classes = useStyles();

  const hideLeftControl = pageIndex === 0;
  const hideRightControl = pageIndex === children.length - 1;

  const pageRefs = React.useRef<React.RefObject<HTMLDivElement>[]>([]);
  pageRefs.current = Array(children.length)
    .fill(0)
    .map((_, i) => pageRefs.current[i] || React.createRef());

  // Used to place the width/height of the largest child. We choose the largest child such that the
  // carousel's width/height don't grow/shrink as users navigate between elements of the carousel.
  const initialMaxChildWidth: MAX_WIDTH_HEIGHT = undefined;
  const initialMaxChildHeight: MAX_WIDTH_HEIGHT = undefined;
  const [maxChildWidth, setMaxChildWidth] = React.useState<MAX_WIDTH_HEIGHT>(initialMaxChildWidth);
  const [maxChildHeight, setMaxChildHeight] = React.useState<MAX_WIDTH_HEIGHT>(
    initialMaxChildHeight,
  );
  const [shouldRemeasureChildren, setShouldRemeasureChildren] = React.useState<number>(0);

  React.useLayoutEffect((): void => {
    // PROBLEM: If the children do change, we try to remeasure. However, the previous maxChildWidth/Height
    // constrain the widths of the new children. That is, if the new children are larger than the previous
    // maxChildWidth/Height, those changes will not be reflected.
    // SOLUTION: We will use the change of children to trigger a chain re-rerender with our `shouldRemeasureChildren`
    // flag. We reset the `maxChildWidth` and `maxChildHeight` to the initial conditions so it computes properly

    setMaxChildWidth(initialMaxChildWidth);
    setMaxChildHeight(initialMaxChildHeight);
    setShouldRemeasureChildren(shouldRemeasureChildren + 1);
  }, [children]);

  React.useLayoutEffect((): void => {
    // Don't try to calculate dimensions if they're being dictated by our parent
    if (windowDimensions) {
      return;
    }

    let foundMaxChildWidth = -Infinity;
    let foundMaxChildHeight = -Infinity;

    pageRefs.current.forEach((ref) => {
      const { current } = ref;
      if (!current) {
        return;
      }

      const { width, height } = current.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(current);
      const getMarginQuantity = (direction: "Top" | "Right" | "Bottom" | "Left"): number =>
        parseInt(computedStyle.getPropertyValue(`margin${direction}`), 10);

      const widthFromMargin = getMarginQuantity("Right") + getMarginQuantity("Left");
      const outerWidth = width + (isNaN(widthFromMargin) ? 0 : widthFromMargin);
      if (outerWidth > foundMaxChildWidth) {
        foundMaxChildWidth = width;
      }

      const heightFromMargin = getMarginQuantity("Top") + getMarginQuantity("Bottom");
      const outerHeight = height + (isNaN(heightFromMargin) ? 0 : heightFromMargin);
      if (outerHeight > foundMaxChildHeight) {
        foundMaxChildHeight = height;
      }
    });

    setMaxChildWidth(foundMaxChildWidth);
    setMaxChildHeight(foundMaxChildHeight);
  }, [children.length, shouldRemeasureChildren]);

  const leftControlEl = (
    <div
      className={cx(classes.control, classes.leftControl, {
        [classes.controlHidden]: hideLeftControl,
      })}
      onClick={onLeftControlClick}
    >
      {leftControl}
    </div>
  );

  const rightControlEl = (
    <div
      className={cx(classes.control, classes.rightControl, {
        [classes.controlHidden]: hideRightControl,
      })}
      onClick={onRightControlClick}
    >
      {rightControl}
    </div>
  );

  const widthAndHeightStyles: React.CSSProperties = {
    width: windowDimensions?.width ?? maxChildWidth,
    height: windowDimensions?.height ?? maxChildHeight,
  };

  const childrenEls = children.map((child, i) => (
    <div
      key={i}
      ref={pageRefs.current[i]}
      style={{
        ...widthAndHeightStyles,
        // At the very edge of the carousel, prevent interaction with hidden pages.
        pointerEvents: i === pageIndex ? "auto" : "none",
      }}
      cw-id="suggested-time-tile"
    >
      {child}
    </div>
  ));

  return (
    <div className={classes.container}>
      {leftControlEl}

      <div className={classes.carouselViewport} style={widthAndHeightStyles}>
        <div
          className={classes.positioningContainer}
          style={{
            left: `-${((windowDimensions?.width ?? maxChildWidth) || 0) * pageIndex}px`,
          }}
        >
          {childrenEls}
        </div>
      </div>

      {rightControlEl}
    </div>
  );
};
